import { Modal, Form, message } from 'antd';
import TextArea from 'antd/es/input/TextArea';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LOCAL_EN } from '@/config';
import { ResolveIcon } from '../Icons';
import { useResolveActionOrder, useRtoRequestOrder } from '@/hooksApi/useOrder';
import { OrderResolveAction, OrderRtoRequest, OrderStatusTab } from '@/types';
import { useQueryClient } from '@tanstack/react-query';
import { useResolveModalStore } from '@/hooks';

export interface ResolveModalProps {
  visible: boolean;
  onClose: () => void;
  platformCode?: string;
  comment?: string;
  commentDate?: string;
  orderId?: string | number;
}

const ResolveModal: React.FC<ResolveModalProps> = ({
  visible,
  onClose,
  orderId,
  platformCode,
  comment,
  commentDate,
}) => {
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const [submitAction, setSubmitAction] = useState<'resolve' | 'return_to_origin'>('resolve');
  const pendingActionRef = React.useRef<'resolve' | 'return_to_origin'>('resolve');

  const { mutate: resolveActionOrder, isLoading: resolving } = useResolveActionOrder();
  const { mutate: requestRtoOrder, isLoading: requestingRto } = useRtoRequestOrder();

  const orderValue = useResolveModalStore((state) => state.order_value);
  const fullName = useResolveModalStore((state) => state.full_name);
  const phone = useResolveModalStore((state) => state.phone);
  const addressTitle = useResolveModalStore((state) => state.addressTitle);

  const invalidateOrderList = () => {
    queryClient.invalidateQueries(['orders-list']);
    queryClient.invalidateQueries([`orders-${OrderStatusTab.ON_HOLD}`]);
    queryClient.invalidateQueries([`order-${OrderStatusTab.ON_HOLD}`]);
  };

  const handleFormSubmit = (values: { comment?: string }) => {
    const action = pendingActionRef.current;
    if (action === 'resolve') {
      if (!values.comment) {
        message.error(t('please_fill_your_comment'));
        return;
      }
      resolveActionOrder(
        { orderId: orderId?.toString() || '', resolveAction: { merchant_action_required_merchant_text: values.comment! } as OrderResolveAction },
        {
          onSuccess: () => { invalidateOrderList(); message.success(t('order_resolved_successfully')); form.resetFields(); onClose(); },
          onError: () => { invalidateOrderList(); message.error(t('operation_failed_please_try_again')); },
        },
      );
    } else {
      requestRtoOrder(
        { orderId: orderId?.toString() || '', rtoRequest: { merchant_action_required_merchant_text: values.comment || '' } as OrderRtoRequest },
        {
          onSuccess: () => { invalidateOrderList(); message.success(t('rto_request_successfully_submitted')); form.resetFields(); onClose(); },
          onError: () => { invalidateOrderList(); message.error(t('operation_failed_please_try_again')); },
        },
      );
    }
  };

  const isLTR = i18n.language === LOCAL_EN;

  return (
    <Modal
      open={visible}
      onCancel={() => { form.resetFields(); onClose(); }}
      title={null}
      footer={null}
      closable={false}
      centered
      width={640}
      styles={{
        content: { borderRadius: 20, padding: 0, overflow: 'hidden' },
        body: { padding: 0 },
      }}
    >
      <Form form={form} layout='vertical' onFinish={handleFormSubmit}>
        {/* ─── Header ─── */}
        <div className='flex items-center justify-between px-7 pt-6 pb-5'>
          <div className='flex items-baseline gap-2'>
            <span className='text-[15px] font-bold text-[rgb(57,57,60)] uppercase tracking-wide'>
              {t('resolve')}
            </span>
            <span className='text-[15px] font-normal text-[rgb(115,123,140)]'>
              {platformCode}
            </span>
          </div>
          <button
            type='button'
            onClick={() => { form.resetFields(); onClose(); }}
            className='w-8 h-8 flex items-center justify-center rounded-lg text-[rgb(115,123,140)] hover:bg-[rgb(245,245,243)] hover:text-[rgb(57,57,60)] transition-colors cursor-pointer'
          >
            <svg width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='2' strokeLinecap='round' strokeLinejoin='round'><line x1='18' y1='6' x2='6' y2='18'/><line x1='6' y1='6' x2='18' y2='18'/></svg>
          </button>
        </div>

        <div className='h-px bg-[rgb(232,230,227)]' />

        {/* ─── Body ─── */}
        <div className='px-7 py-5 space-y-6'>

          {/* Order Info */}
          <div>
            <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)]'>
              {String(t('order_info'))}
            </span>
            <div className='mt-3'>
              <span className='text-[12px] text-[rgb(115,123,140)]'>{String(t('value'))}</span>
              <div className='flex items-baseline gap-1 mt-0.5' style={{ direction: 'ltr' }}>
                <span className='text-[18px] font-bold text-[rgb(57,57,60)]'>
                  {orderValue.toLocaleString()}
                </span>
                <span className='text-[13px] font-medium text-[rgb(115,123,140)] uppercase'>
                  {String(t('iqd'))}
                </span>
              </div>
            </div>
          </div>

          {/* Customer Details */}
          <div>
            <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)]'>
              {String(t('customer_details'))}
            </span>
            <div className='grid grid-cols-3 gap-4 mt-3'>
              <div>
                <span className='text-[12px] text-[rgb(115,123,140)]'>{String(t('name'))}</span>
                <p className='text-[14px] font-medium text-[rgb(57,57,60)] mt-0.5 m-0'>{fullName}</p>
              </div>
              <div>
                <span className='text-[12px] text-[rgb(115,123,140)]'>{String(t('phone_number'))}</span>
                <p className='text-[14px] font-medium text-[rgb(57,57,60)] mt-0.5 m-0' style={{ direction: 'ltr', textAlign: isLTR ? 'left' : 'right' }}>{phone}</p>
              </div>
              <div>
                <span className='text-[12px] text-[rgb(115,123,140)]'>{String(t('address'))}</span>
                <p className='text-[14px] font-medium text-[rgb(57,57,60)] mt-0.5 m-0'>{addressTitle}</p>
              </div>
            </div>
          </div>

          {/* Hold Delivery Attempt Reason — boxy support comment */}
          {comment && (
            <div>
              <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)]'>
                {String(t('failed_delivery_attempt'))}
              </span>
              <div className='mt-3 space-y-2'>
                {comment && (
                  <div className='flex items-start gap-2 bg-[rgb(245,245,243)] rounded-lg px-3 py-2.5'>
                    <span className='text-[11px] font-semibold text-[rgb(237,168,48)] bg-[rgba(237,168,48,0.12)] rounded px-1.5 py-0.5 shrink-0 uppercase'>
                      {String(t('support', { defaultValue: 'Support' }))}
                    </span>
                    <div className='flex-1 min-w-0'>
                      <span className='text-[13px] text-[rgb(57,57,60)]'>{comment}</span>
                      {commentDate && (
                        <span className='block text-[11px] text-[rgb(155,163,175)] mt-1'>{commentDate}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Add Your Comment */}
          <div>
            <span className='text-[11px] font-bold uppercase tracking-widest text-[rgb(115,123,140)]'>
              {String(t('add_your_comment'))}
            </span>
            <div className='mt-3'>
              <Form.Item name='comment' className='mb-0'>
                <TextArea
                  className='rounded-xl'
                  style={{
                    border: '1px solid rgb(232,230,227)',
                    padding: '12px 14px',
                    fontSize: 14,
                    resize: 'none',
                  }}
                  placeholder={String(t('do_not_ring_door'))}
                  autoSize={{ minRows: 3, maxRows: 6 }}
                />
              </Form.Item>
            </div>
          </div>
        </div>

        {/* ─── Footer ─── */}
        <div className='flex items-center justify-end gap-3 px-7 pb-7 pt-2'>
          <button
            type='button'
            className='flex items-center justify-center gap-2 h-10 px-5 rounded-[10px] border border-solid border-[rgb(232,230,227)] bg-white text-[rgb(57,57,60)] text-[13px] font-semibold uppercase cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(245,245,243)] transition-colors'
            disabled={requestingRto}
            onClick={() => { pendingActionRef.current = 'return_to_origin'; setSubmitAction('return_to_origin'); form.submit(); }}
          >
            {requestingRto && <span className='h-3.5 w-3.5 rounded-full border-2 border-[rgb(57,57,60)] border-t-transparent animate-spin' />}
            {t('request_rto')}
          </button>
          <button
            type='button'
            className='flex items-center justify-center gap-2 h-10 px-5 rounded-[10px] bg-[rgb(237,224,74)] text-[rgb(57,57,60)] text-[13px] font-semibold uppercase cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed hover:bg-[rgb(230,217,60)] transition-colors'
            disabled={resolving}
            onClick={() => { pendingActionRef.current = 'resolve'; setSubmitAction('resolve'); form.submit(); }}
          >
            {resolving ? <span className='h-3.5 w-3.5 rounded-full border-2 border-[rgb(57,57,60)] border-t-transparent animate-spin' /> : <ResolveIcon width={16} height={16} />}
            {t('resolve')}
          </button>
        </div>
      </Form>
    </Modal>
  );
};

export default ResolveModal;
